import {
  EuiButton,
  EuiButtonEmpty,
  EuiEmptyPrompt,
  EuiFlexGroup,
  EuiFlexItem,
  EuiPanel,
  EuiSpacer,
} from "@elastic/eui";
import {
  ErrMsg,
  CancelImage,
  AuthImage,
  WarningImage,
  useAnketaI18n,
} from "../..";
import { useLocation, useNavigate } from "react-router";
import { ReactNode } from "react";

export const WarningPage = ({
  error,
  actions,
}: {
  error?: string;
  actions?: ReactNode;
}): JSX.Element => {
  const { translate } = useAnketaI18n();
  const location = useLocation();
  const navigate = useNavigate();

  // When the caller doesn't supply actions, offer a sensible recovery path so
  // the error page is never a dead end: retry (reload) + go back.
  const defaultActions = (
    <EuiFlexGroup
      gutterSize="s"
      justifyContent="center"
      responsive={false}
      wrap
    >
      <EuiFlexItem grow={false}>
        <EuiButton
          iconType="refresh"
          fill
          onClick={() => window.location.reload()}
        >
          {translate("anketa.page.error.retry", { defaultMessage: "Retry" })}
        </EuiButton>
      </EuiFlexItem>
      <EuiFlexItem grow={false}>
        <EuiButtonEmpty iconType="arrowLeft" onClick={() => navigate(-1)}>
          {translate("anketa.page.error.back", { defaultMessage: "Go back" })}
        </EuiButtonEmpty>
      </EuiFlexItem>
    </EuiFlexGroup>
  );

  const getCurrentView = (): string => {
    const pathItems = location.pathname
      .split("/")
      .filter((item) => item !== "")[0];

    return translate(`anketa.${pathItems}`);
  };

  const generateErrorMsg = (): ErrMsg => {
    switch (error) {
      case "403":
        return {
          msg: (
            <>
              <h3>404</h3>
              <p>{translate("anketa.page.404")}</p>
            </>
          ),
          color: "warning",
          image: <CancelImage />,
        };
      case "511":
        return {
          msg: (
            <>
              <h3>511</h3>
              <p>{translate("anketa.page.511")}</p>
            </>
          ),
          color: "primary",
          image: <AuthImage />,
        };
      default:
        return {
          msg: (
            <>
              <p>{translate("anketa.page.error.body")}</p>
              <p>{error && error}</p>
            </>
          ),
          color: "danger",
          image: <WarningImage />,
        };
    }
  };
  return (
    <EuiFlexGroup gutterSize="none" direction="column" responsive={false}>
      <EuiSpacer size="l" />
      <EuiFlexItem style={{ height: "fit-content" }}>
        <EuiFlexGroup gutterSize="l">
          <EuiFlexItem grow={3}>
            <EuiPanel
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              tabIndex={0}
              hasShadow={true}
            >
              {generateErrorMsg().image}
            </EuiPanel>
          </EuiFlexItem>
          <EuiFlexItem>
            <EuiPanel color={generateErrorMsg().color}>
              <EuiEmptyPrompt
                iconType="alert"
                iconColor={generateErrorMsg().color}
                title={
                  <h2>
                    {translate("anketa.page.error.header", {
                      values: { type: getCurrentView() },
                    })}
                  </h2>
                }
                body={generateErrorMsg().msg}
                actions={actions ?? defaultActions}
              />
            </EuiPanel>
          </EuiFlexItem>
        </EuiFlexGroup>
      </EuiFlexItem>
    </EuiFlexGroup>
  );
};
